import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  FileText,
  File,
  Globe,
  Presentation,
  PlayCircle,
  MessageCircle,
  Shield,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { FavoriteButton } from '../../../components/FavoriteButton';
import { useAuth } from '@/auth/hooks/useAuth';
import { queryKeys } from '@/lib/query-keys';
import { api, API_BASE } from '@/lib/api';

/**
 * Build the href for a resource card.
 * If the admin set a page URL (sourceUrl), use it directly — it's either an absolute
 * URL or a path on the platform. Otherwise, use the /view endpoint which generates a
 * presigned S3 URL for file-backed documents.
 */
function resourceHref(resource: {
  fileKey: string | null;
  sourceUrl: string | null;
  docId: string;
}): { href: string; isExternal: boolean } {
  if (resource.sourceUrl) {
    const isAbsoluteUrl = resource.sourceUrl.startsWith('http');
    return { href: resource.sourceUrl, isExternal: isAbsoluteUrl };
  }
  if (resource.fileKey) {
    return {
      href: `${API_BASE}/arches/learn-about-fias/documents/${resource.docId}/view`,
      isExternal: true,
    };
  }
  return { href: '#', isExternal: false };
}

interface ResourceCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  buttonLabel: string;
  href: string;
  external?: boolean;
}

function ResourceCard({
  icon,
  title,
  description,
  buttonLabel,
  href,
  external,
}: ResourceCardProps) {
  return (
    <Card className="h-full">
      <CardContent className="flex flex-col items-center justify-between gap-6 p-8 h-full text-center">
        <div className="flex flex-col items-center gap-6">
          <div className="text-primary">{icon}</div>
          <div className="space-y-2">
            <h3 className="text-lg font-semibold">{title}</h3>
            <p className="text-sm text-muted-foreground">{description}</p>
          </div>
        </div>
        {external ? (
          <a href={href} target="_blank" rel="noopener noreferrer" className="w-full">
            <Button variant="secondary" size="lg" className="w-full">
              {buttonLabel}
            </Button>
          </a>
        ) : (
          <Link to={href} className="w-full">
            <Button variant="secondary" size="lg" className="w-full">
              {buttonLabel}
            </Button>
          </Link>
        )}
      </CardContent>
    </Card>
  );
}

const ICON_MAP: Record<string, React.ReactNode> = {
  'file-text': <FileText className="h-10 w-10" />,
  file: <File className="h-10 w-10" />,
  globe: <Globe className="h-10 w-10" />,
  presentation: <Presentation className="h-10 w-10" />,
  'play-circle': <PlayCircle className="h-10 w-10" />,
};

function getIcon(iconType: string | null): React.ReactNode {
  if (iconType && ICON_MAP[iconType]) return ICON_MAP[iconType];
  return <FileText className="h-10 w-10" />;
}

export function LearnAboutFiasResourcesPage() {
  const { user } = useAuth();
  const isAdmin = useMemo(() => user?.roles?.includes('admin') ?? false, [user?.roles]);

  const { data: dynamicResources, isLoading } = useQuery({
    queryKey: queryKeys.learnAboutFias.resources(),
    queryFn: () => api.getLearnAboutFiasResources(),
  });

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <div className="flex flex-col items-center gap-4 mb-8">
        <div className="flex items-center gap-2">
          <h1 className="text-3xl font-bold text-center">Learn About Fias</h1>
          <FavoriteButton archeId="arche_learn_about_fias" />
        </div>
        <p className="text-muted-foreground text-center">
          Explore resources to learn more about Fias and the future of monetizing AI.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {isLoading ? (
          <>
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="h-full">
                <CardContent className="flex flex-col items-center gap-6 p-8">
                  <Skeleton className="h-10 w-10 rounded" />
                  <div className="space-y-2 w-full">
                    <Skeleton className="h-5 w-3/4 mx-auto" />
                    <Skeleton className="h-4 w-full" />
                  </div>
                  <Skeleton className="h-10 w-full" />
                </CardContent>
              </Card>
            ))}
          </>
        ) : (
          dynamicResources?.resources.map((resource) => {
            const { href, isExternal } = resourceHref(resource);

            return (
              <ResourceCard
                key={resource.docId}
                icon={getIcon(resource.iconType)}
                title={resource.title}
                description={resource.description ?? ''}
                buttonLabel={resource.buttonLabel ?? (isExternal ? 'View' : 'Learn More')}
                href={href}
                external={isExternal}
              />
            );
          })
        )}
      </div>

      <div className="flex justify-center mt-8">
        <Card className="w-full sm:w-1/2">
          <CardContent className="flex flex-col items-center gap-6 p-8 text-center">
            <MessageCircle className="h-10 w-10 text-primary" />
            <div className="space-y-2">
              <h3 className="text-lg font-semibold">Ask AI</h3>
              <p className="text-sm text-muted-foreground">
                Have questions? Ask our AI assistant about Fias.
              </p>
            </div>
            <Link to="/a/arche_learn_fias_chat" className="w-full">
              <Button variant="secondary" size="lg" className="w-full">
                Start Conversation
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      {isAdmin && (
        <div className="flex justify-center mt-4">
          <Link to="/a/arche_learn_about_fias/admin">
            <Button variant="outline" size="sm">
              <Shield className="mr-2 h-4 w-4" />
              Manage Assets
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
}
